import React from "react";

/**
 * Before / After slider. Uses placeholder images that can be swapped later.
 * Drag (or move) the orange handle to reveal the "after" image.
 */
const BEFORE = "https://customer-assets.emergentagent.com/job_clean-solutions-35/artifacts/1sd36wyz_IMG_4925.jpeg";
const AFTER = "https://customer-assets.emergentagent.com/job_clean-solutions-35/artifacts/vy6a42xb_IMG_4926.jpeg";

export default function BeforeAfter() {
  const [pos, setPos] = React.useState(50);
  const wrapRef = React.useRef(null);
  const dragging = React.useRef(false);

  const onMove = (clientX) => {
    const el = wrapRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    let p = ((clientX - rect.left) / rect.width) * 100;
    p = Math.max(0, Math.min(100, p));
    setPos(p);
  };

  return (
    <section id="gallery" className="py-20 md:py-28 bg-white" data-testid="ba-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <div className="font-script text-3xl text-[var(--fc-orange)]">See the difference</div>
          <h2 className="font-heading text-4xl sm:text-5xl font-bold text-[var(--fc-blue-dark)] mt-1">
            Before &amp; After
          </h2>
          <p className="mt-4 max-w-2xl mx-auto text-slate-600 text-lg">
            Drag the handle to reveal what a Faithfully Clean wash can do. Real photos from our jobs will be added here soon.
          </p>
        </div>

        <div
          ref={wrapRef}
          className="relative w-full aspect-[16/9] rounded-3xl overflow-hidden shadow-xl shadow-blue-900/15 border border-blue-100 select-none cursor-ew-resize bg-slate-200"
          onMouseDown={() => (dragging.current = true)}
          onMouseUp={() => (dragging.current = false)}
          onMouseLeave={() => (dragging.current = false)}
          onMouseMove={(e) => dragging.current && onMove(e.clientX)}
          onTouchStart={(e) => onMove(e.touches[0].clientX)}
          onTouchMove={(e) => onMove(e.touches[0].clientX)}
          data-testid="ba-slider"
        >
          {/* after (background) */}
          <img
            src={AFTER}
            alt="After pressure washing"
            className="absolute inset-0 w-full h-full object-cover"
            draggable={false}
          />
          <div className="absolute top-4 right-4 bg-white/95 text-[var(--fc-blue-dark)] font-heading font-bold uppercase tracking-wider text-sm px-3 py-1 rounded-full">
            After
          </div>

          {/* before (clipped) */}
          <div
            className="absolute inset-0 overflow-hidden"
            style={{ width: `${pos}%` }}
          >
            <img
              src={BEFORE}
              alt="Before pressure washing"
              className="absolute inset-0 h-full w-[100vw] max-w-none object-cover"
              draggable={false}
              style={{ width: `${(100 / pos) * 100}%` }}
            />
            <div className="absolute top-4 left-4 bg-[var(--fc-blue-dark)] text-white font-heading font-bold uppercase tracking-wider text-sm px-3 py-1 rounded-full">
              Before
            </div>
          </div>

          {/* handle */}
          <div
            className="absolute top-0 bottom-0"
            style={{ left: `calc(${pos}% - 2px)`, width: 4 }}
          >
            <div className="w-full h-full bg-white" />
            <button
              type="button"
              aria-label="Drag to compare"
              data-testid="ba-handle-btn"
              className="ba-handle absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-12 h-12 rounded-full grid place-items-center text-white"
              onMouseDown={() => (dragging.current = true)}
              onTouchStart={() => (dragging.current = true)}
            >
              <span className="text-lg leading-none">‹›</span>
            </button>
          </div>
        </div>

        <div className="mt-6 flex items-center justify-center gap-4">
          <div className="inline-flex items-center gap-2 bg-emerald-50 border border-emerald-200 text-emerald-800 rounded-full px-4 py-1.5 font-bold text-sm">
            <span className="w-2 h-2 rounded-full bg-emerald-500" /> 100% Satisfaction Guaranteed
          </div>
          <div className="inline-flex items-center gap-2 bg-amber-50 border border-amber-200 text-amber-800 rounded-full px-4 py-1.5 font-bold text-sm">
            ★ Soft Wash Experts
          </div>
        </div>
      </div>
    </section>
  );
}
